<template>
    <div class="ShiftsTable">
        <BaseDashboardTable
            ref="dashboardTable"
            :columns="columns"
            :items="formattedShifts"
            :sort="sortState"
            :empty-message="'Keine Schichten vorhanden'"
            :pagination="paginationConfig"
            :container-height="containerHeight"
            :use-item-virtualization="true"
            :isClickable="true"
            :show-legend="true"
            :legend-items="legendItems"
            :searchable-columns="[
                'driver',
                'sequenceNumber',
                'licenseNumber',
                'startAt',
                'endAt',
                'finalTotalAmount',
                'tips',
                'cashAmount',
            ]"
            :search-placeholder="'Schichten durchsuchen...'"
            page-id="shifts-dashboard"
            table-id="shifts-table"
            :show-column-toggler="true"
            @sort-changed="handleSortChanged"
            @row-click="handleShiftClick"
            @toggle-expand="handleToggleExpand"
            @row-hover="(args) => $emit('onRowHover', args)"
            @row-leave="(args) => $emit('onRowLeave', args)"
            @expand-on-match="handleExpandOnMatch"
            @search-cleared="handleSearchCleared"
            @page-changed="handlePageChanged"
        >
            <template #title>
                <h3 id="shifts-table-header">Schichten</h3>
            </template>
            <template #column-tips="{ item }">
                {{ item.tips }}
            </template>

            <template #column-gesamtumsatz="{ item }">
                <div class="amount-cell">
                    <span
                        :class="{
                            'warning-text': item.hasWarning,
                            'empty-trip-bg': item.isEmptyTrip,
                        }"
                    >
                        {{ item.gesamtumsatz }}
                    </span>
                </div>
            </template>
            <template #column-driver="{ item }">
                <DriverPopover
                    v-if="!item._isChild && item.driver !== 'Unangemeldet'"
                    :driver-name="item.driver"
                    :driver-number="item.driverNumber || 'unknown'"
                    :driver-id="item.driverId || null"
                    class="tutorial-driver-name"
                    @filter="handleDriverFilter"
                    @changeDriver="(event) => handleChangeDriver(event, item)"
                    @click.stop
                />
                <span v-else-if="!item._isChild" class="unregistered-driver">
                    {{ item.driver }}
                </span>
            </template>

            <template #column-licenseNumber="{ item }">
                <LicenseNumberPopover
                    v-if="!item._isChild"
                    :license-number="item.licenseNumber"
                    :car-id="item.originalShift?.car?.id || item.originalShift?.car?.uuid"
                    :car-uuid="item.originalShift?.car?.uuid"
                    :concession="item.concession"
                    @filter="handleCarFilter"
                    @changeCar="handleChangeCar"
                    @click.stop
                />
            </template>

            <template #column-concession="{ item }">
                <ConcessionPopover
                    v-if="item.concession && !item._isChild"
                    :concession="item.concession"
                    :car-id="item.licenseNumber"
                    :car-uuid="item.car?.uuid"
                    @filter="handleConcessionFilter"
                />
            </template>

            <template #column-finalTotalAmount="{ item }">
                <div class="amount-cell">
                    <span
                        :class="{
                            'warning-text': item.hasWarning,
                            'empty-trip-bg': item.isEmptyTrip,
                            'flatrate-trip-bg': item.isFlatRate,
                        }"
                    >
                        {{ item.finalTotalAmount }}
                    </span>
                </div>
            </template>

            <template #column-subtractedTotalAmount="{ item }">
                <span
                    v-if="item.subtractedTotalAmount !== '--:--'"
                    :class="{
                        'warning-text': item._subtractedTotalAmount < 0,
                        'positive-text': item._subtractedTotalAmount > 0,
                        'tutorial-correction-cell': true,
                    }"
                >
                    {{ item.subtractedTotalAmount }}
                </span>
                <span v-else>{{ item.subtractedTotalAmount }}</span>
            </template>

            <template #column-cashAmount="{ item }">
                <div class="amount-cell">
                    <span
                        :class="{
                            'warning-text': item.hasWarning,
                            'empty-trip-bg': item.isEmptyTrip,
                        }"
                    >
                        {{ item.cashAmount }}
                    </span>
                </div>
            </template>

            <template #column-paymentMethod_uber="{ item }">
                <div class="amount-cell">
                    <span>{{ item.paymentMethod_uber || '--:--' }}</span>
                </div>
            </template>

            <template #column-paymentMethod_bolt="{ item }">
                <div class="amount-cell">
                    <span>{{ item.paymentMethod_bolt || '--:--' }}</span>
                </div>
            </template>

            <template #column-paymentMethod_bliq="{ item }">
                <div class="amount-cell">
                    <span>{{ item.paymentMethod_bliq || '--:--' }}</span>
                </div>
            </template>

            <template #column-paymentMethod_freenow="{ item }">
                <div class="amount-cell">
                    <span>{{ item.paymentMethod_freenow || '--:--' }}</span>
                </div>
            </template>

            <template #column-paymentMethod_card="{ item }">
                <div class="amount-cell">
                    <span>{{ item.paymentMethod_card || '--:--' }}</span>
                </div>
            </template>

            <template #column-paymentMethod_coupon="{ item }">
                <div class="amount-cell">
                    <span>{{ item.paymentMethod_coupon || '--:--' }}</span>
                </div>
            </template>

            <template #column-paymentMethod_taxieupayment="{ item }">
                <div class="amount-cell">
                    <span>{{ item.paymentMethod_taxieupayment || '--:--' }}</span>
                </div>
            </template>

            <template #column-paymentMethod_taxideutschland="{ item }">
                <div class="amount-cell">
                    <span>{{ item.paymentMethod_taxideutschland || '--:--' }}</span>
                </div>
            </template>

            <template #column-paymentMethod_invoice="{ item }">
                <div class="amount-cell">
                    <span>{{ item.paymentMethod_invoice || '--:--' }}</span>
                </div>
            </template>

            <template #column-details="{ item }">
                <Button
                    v-if="(isMobile || isTablet) && !item._isChild"
                    size="small"
                    variant="table"
                    v-tooltip="getDetailsTooltip(item)"
                    @onClick="openDetailsModal(item)"
                >
                    <i class="ri-information-line"></i>
                </Button>
            </template>

            <template #column-comments="{ item }">
                <span v-if="!item._isChild" @click.stop>
                    <Button
                        size="small"
                        variant="table"
                        v-tooltip="'Kommentare anzeigen'"
                        @onClick="openCommentsModal(item)"
                    >
                        <i class="ri-chat-3-line"></i>
                    </Button>
                </span>
            </template>
        </BaseDashboardTable>

        <ShiftCommentsModal
            :show="showCommentsModal"
            :shift="selectedShiftForComments"
            @close="closeCommentsModal"
        />

        <Modal
            :show="showDetailsModal"
            :title="'Schichtendetails'"
            @onModalClose="closeDetailsModal"
        >
            <div v-if="selectedShiftForDetails" class="shift-details-modal">
                <div class="detail-row">
                    <span class="label">Fahrer:</span>
                    <span class="value">{{ selectedShiftForDetails.driver }}</span>
                </div>
                <div class="detail-row">
                    <span class="label">Schicht:</span>
                    <span class="value">{{ selectedShiftForDetails.sequenceNumber }}</span>
                </div>
                <div class="detail-row">
                    <span class="label">Kennzeichen:</span>
                    <span class="value">{{ selectedShiftForDetails.licenseNumber }}</span>
                </div>
                <div class="detail-row">
                    <span class="label">Start:</span>
                    <span class="value">{{ selectedShiftForDetails.startAt }}</span>
                </div>
                <div class="detail-row">
                    <span class="label">Ende:</span>
                    <span class="value">{{ selectedShiftForDetails.endAt }}</span>
                </div>
                <div class="detail-row">
                    <span class="label">Zeit:</span>
                    <span class="value">{{ selectedShiftForDetails.duration }}</span>
                </div>
                <div class="detail-row">
                    <span class="label">Ges.KM:</span>
                    <span class="value">{{ selectedShiftForDetails.totalDistance }}</span>
                </div>
                <div class="detail-row">
                    <span class="label">Bes.KM:</span>
                    <span class="value">{{ selectedShiftForDetails.hiredDistance }}</span>
                </div>
                <div class="detail-row">
                    <span class="label">Leer KM:</span>
                    <span class="value">{{ selectedShiftForDetails.forHireDistance }}</span>
                </div>
                <div class="detail-row">
                    <span class="label">Zuschl.:</span>
                    <span class="value">{{ selectedShiftForDetails.finalExtrasAmount }}</span>
                </div>
                <div class="detail-row">
                    <span class="label">Korrektur:</span>
                    <span class="value">{{ selectedShiftForDetails.subtractedTotalAmount }}</span>
                </div>
                <div class="detail-row">
                    <span class="label">Trinkgeld:</span>
                    <span class="value">{{ selectedShiftForDetails.tips }}</span>
                </div>
                <div class="detail-row">
                    <span class="label">Bar:</span>
                    <span class="value">{{ selectedShiftForDetails.cashAmount }}</span>
                </div>
                <div class="detail-row">
                    <span class="label">Umsatz:</span>
                    <span class="value">{{ selectedShiftForDetails.finalTotalAmount }}</span>
                </div>
                <div v-if="selectedShiftForDetails.concession" class="detail-row">
                    <span class="label">Konzession:</span>
                    <span class="value">{{ selectedShiftForDetails.concession }}</span>
                </div>
                <div v-if="selectedShiftForDetails.vatRate" class="detail-row">
                    <span class="label">USt.:</span>
                    <span class="value">{{ selectedShiftForDetails.vatRate }}</span>
                </div>
            </div>
        </Modal>
    </div>
</template>

<script>
import { format } from 'date-fns';
import { round2d } from '@/lib/helper';
import { mapGetters } from 'vuex';
import BaseDashboardTable from '@/components/BaseDashboardTable';
import Button from '@/components/widgets/Button';
import DriverPopover from '@/components/DriverPopover.vue';
import LicenseNumberPopover from '@/components/LicenseNumberPopover.vue';
import ConcessionPopover from '@/components/ConcessionPopover.vue';
import Modal from '@/components/widgets/Modal.vue';
import ShiftCommentsModal from './ShiftCommentsModal.vue';
import resizeMixin from '@/mixins/resizeMixin';

export default {
    name: 'ShiftsTable',
    mixins: [resizeMixin],
    components: {
        BaseDashboardTable,
        Button,
        DriverPopover,
        LicenseNumberPopover,
        ConcessionPopover,
        Modal,
        ShiftCommentsModal,
    },
    props: {
        shifts: {
            type: Array,
            default: () => [],
        },
        showAllTrips: {
            type: Boolean,
            default: false,
        },
        containerHeight: {
            type: String,
            default: 'calc(100vh - 300px)',
        },
        hasConcession: {
            type: Boolean,
            default: false,
        },
        usedPaymentMethods: {
            type: Set,
            default: () => new Set(),
        },
    },
    data() {
        return {
            sortState: {
                column: 'startAt',
                ascending: false,
            },
            paginationConfig: {
                enabled: true,
                pageSize: 200,
                currentPage: 1,
            },
            showDetailsModal: false,
            selectedShiftForDetails: null,
            showCommentsModal: false,
            selectedShiftForComments: null,
            baseColumns: [
                {
                    key: 'driver',
                    label: 'Fahrer Name',
                    priority: 1,
                },
                {
                    key: 'sequenceNumber',
                    label: 'Schicht',
                    priority: 2,
                },
                {
                    key: 'licenseNumber',
                    label: 'Kennzeichen',
                    priority: 1,
                },
                {
                    key: 'startAt',
                    label: 'Start',
                    priority: 2,
                },
                {
                    key: 'endAt',
                    label: 'Ende',
                    priority: 2,
                },
                {
                    key: 'duration',
                    label: 'Zeit',
                    sortable: true,
                    priority: 2,
                },
                {
                    key: 'dauerDez',
                    label: 'Dauer (Dez.)',
                    sortable: true,
                    align: 'right',
                    priority: 4,
                },
                {
                    key: 'totalDistance',
                    label: 'Ges.KM',
                    align: 'right',
                    priority: 4,
                },
                {
                    key: 'hiredDistance',
                    label: 'Bes.KM',
                    align: 'right',
                    priority: 4,
                },
                {
                    key: 'forHireDistance',
                    label: 'Leer KM',
                    align: 'right',
                    priority: 4,
                },
                {
                    key: 'finalExtrasAmount',
                    label: 'Zuschl.',
                    align: 'right',
                    priority: 4,
                },
                {
                    key: 'subtractedTotalAmount',
                    label: 'Korrektur',
                    align: 'right',
                    priority: 4,
                },
                {
                    key: 'tips',
                    label: 'Trinkgeld',
                    align: 'right',
                    priority: 4,
                },
                {
                    key: 'cashAmount',
                    label: 'Bar',
                    align: 'right',
                    priority: 3,
                },
                {
                    key: 'vatRate',
                    label: 'USt.',
                    align: 'right',
                    priority: 3,
                },
                {
                    key: 'finalTotalAmount',
                    label: 'Umsatz',
                    align: 'right',
                    priority: 1,
                },
                {
                    key: 'gesamtumsatz',
                    label: 'Gesamtumsatz',
                    align: 'right',
                    priority: 1,
                },
            ],
            expandedShiftIds: [],
        };
    },
    computed: {
        ...mapGetters(['isAlleinfahrenderEU', 'paymentMethods']),
        ...mapGetters('tablePreferences', ['getDisplayPreferences']),
        hideTripMap() {
            return this.getDisplayPreferences?.hideTripMap || false;
        },
        columns() {
            let columns = [...this.baseColumns];

            const paymentMethodColumns = Array.from(this.usedPaymentMethods)
                .filter((method) => method !== 'cash')
                .map((method) => {
                    const paymentMethod = this.paymentMethods?.find((pm) => pm.value === method);
                    const label = paymentMethod ? paymentMethod.name : method;
                    return {
                        key: `paymentMethod_${method}`,
                        label: label,
                        align: 'right',
                        priority: 3,
                        width: '100px',
                    };
                })
                .sort((a, b) => a.label.localeCompare(b.label));

            const cashIndex = columns.findIndex((col) => col.key === 'cashAmount');
            if (cashIndex !== -1 && paymentMethodColumns.length > 0) {
                columns.splice(cashIndex + 1, 0, ...paymentMethodColumns);
            }

            if (this.hasConcession) {
                const licenseIndex = columns.findIndex((col) => col.key === 'licenseNumber');
                if (licenseIndex !== -1) {
                    columns.splice(licenseIndex + 1, 0, {
                        key: 'concession',
                        label: 'Konzession',
                        width: '8%',
                        priority: 1,
                    });
                }
            }

            if (!this.isAlleinfahrenderEU) {
                columns = columns.filter((col) => col.key !== 'gesamtumsatz');
            }

            if (this.isMobile || this.isTablet) {
                columns.push({
                    key: 'details',
                    label: 'Details',
                    sortable: false,
                    align: 'center',
                    priority: 1,
                    width: '80px',
                });
            }

            columns.push({
                key: 'comments',
                label: 'Kommentare',
                sortable: false,
                align: 'center',
                priority: 1,
                width: '100px',
            });

            return columns;
        },
        formattedShifts() {
            const formattedData = [];

            this.shifts.forEach((shift) => {
                const shiftId = shift.uuid || `shift-${shift.sequenceNumber}`;
                const isExpanded = this.expandedShiftIds.includes(shiftId) || this.showAllTrips;
                const hasError = this.hasError(shift);
                const hasWarning = this.hasWarning(shift);
                const paymentMethodSums = this.calculatePaymentMethodSums(shift);

                const parentRow = {
                    driver: shift.driver?.name || '',
                    driverNumber: shift.driver?.driverNumber || null,
                    driverId: shift.driver?.id || shift.driver?.uuid || null,
                    sequenceNumber: shift.sequenceNumber || '',
                    licenseNumber: shift.licenseNumber || '',
                    concession:
                        shift.concession !== undefined && shift.concession !== null
                            ? String(shift.concession)
                            : '',
                    startAt: format(new Date(shift.startAt), 'dd.MM.yyyy - HH:mm'),
                    endAt: format(new Date(shift.endAt), 'dd.MM.yyyy - HH:mm'),
                    duration: this.formatDuration(shift.startAt, shift.endAt),
                    dauerDez: this.getDecimalDuration(shift.startAt, shift.endAt),
                    totalDistance: shift.totalDistance
                        ? round2d(shift.totalDistance / 1000).format()
                        : '--:--',
                    hiredDistance: shift.hiredDistance
                        ? round2d(shift.hiredDistance / 1000).format()
                        : '--:--',
                    forHireDistance: shift.forHireDistance
                        ? round2d(shift.forHireDistance / 1000).format()
                        : '--:--',
                    finalExtrasAmount: shift.finalExtrasAmount
                        ? `${round2d(shift.finalExtrasAmount / 100).format()}€`
                        : '--:--',
                    subtractedTotalAmount: (() => {
                        const subtracted = this.calculateSubtractedAmount(shift);
                        if (subtracted === 0) return '--:--';
                        const sign = subtracted > 0 ? '+' : '';
                        return `${sign}${round2d(subtracted / 100).format()}€`;
                    })(),
                    finalTotalAmount: `${round2d(shift.finalTotalAmount / 100).format()}€`,
                    tips: this.calculateShiftTips(shift),
                    gesamtumsatz: `${round2d((shift.finalTotalAmount + this.calculateShiftTips(shift, true)) / 100).format()}€`,
                    cashAmount: this.calculateShiftCashAmount(shift),
                    vatRate:
                        shift.vatRate !== undefined && shift.vatRate !== null
                            ? `${shift.vatRate}%`
                            : '--:--',

                    _startAt: new Date(shift.startAt),
                    _endAt: new Date(shift.endAt),
                    _duration: this.getDurationInMinutes(shift.startAt, shift.endAt),
                    _dauerDez: parseFloat(this.getDecimalDuration(shift.startAt, shift.endAt)),
                    _driver: (shift.driver?.name || '').toLowerCase(),
                    _sequenceNumber: shift.sequenceNumber || 0,
                    _licenseNumber: (shift.licenseNumber || '').toLowerCase(),
                    _concession:
                        shift.concession !== undefined && shift.concession !== null
                            ? String(shift.concession)
                            : '',
                    _totalDistance: shift.totalDistance || 0,
                    _hiredDistance: shift.hiredDistance || 0,
                    _forHireDistance: shift.forHireDistance || 0,
                    _finalExtrasAmount: shift.finalExtrasAmount || 0,
                    _subtractedTotalAmount: this.calculateSubtractedAmount(shift),
                    _finalTotalAmount: shift.finalTotalAmount || 0,
                    _tips: this.calculateShiftTips(shift, true),
                    _gesamtumsatz:
                        (shift.finalTotalAmount || 0) + this.calculateShiftTips(shift, true),
                    _cashAmount: this.calculateShiftCashAmount(shift, true),
                    _vatRate:
                        shift.vatRate !== undefined && shift.vatRate !== null
                            ? shift.vatRate
                            : null,
                    paymentMethodSums: paymentMethodSums,
                    _paymentMethodSums: paymentMethodSums,
                    ...Object.keys(paymentMethodSums).reduce((acc, method) => {
                        const amount = paymentMethodSums[method];
                        acc[`paymentMethod_${method}`] =
                            amount > 0 ? `${round2d(amount / 100).format()}€` : '--:--';
                        acc[`_paymentMethod_${method}`] = amount;
                        return acc;
                    }, {}),

                    originalShift: shift,
                    hasError,
                    hasWarning,
                    isEmptyTrip: false,
                    isFlatRate: false,
                    isUnregisteredDriver: !shift.driver?.name,
                    hasGpsData: false,
                    _expanded: isExpanded,
                    _id: shiftId,
                    _hasChildren: shift.trips && shift.trips.length > 0,
                    _isChild: false,
                    _level: 0,
                };

                formattedData.push(parentRow);

                if (isExpanded && shift.trips && shift.trips.length > 0) {
                    shift.trips
                        .sort((a, b) => b.sequenceNumber - a.sequenceNumber)
                        .forEach((trip, index) => {
                            const hasWarning = this.hasTripWarning(trip);
                            const isEmptyTrip =
                                trip.hiredDistance === 0 && trip.finalTotalAmount === 0;
                            const isFlatRate = trip.isFlatRate;

                            let tripDriverName = shift.driver?.name || '';
                            if (tripDriverName === -1 || tripDriverName === '-1') {
                                tripDriverName = 'Unangemeldet';
                            }

                            const hasGpsData = !this.hideTripMap && trip.totalGpsStamps >= 2;

                            const childRow = {
                                driver: tripDriverName,
                                sequenceNumber: trip.sequenceNumber || '',
                                startAt: format(new Date(trip.startAt), 'dd.MM.yyyy - HH:mm'),
                                endAt: format(new Date(trip.endAt), 'dd.MM.yyyy - HH:mm'),
                                duration: this.formatDuration(trip.startAt, trip.endAt),
                                dauerDez: this.getDecimalDuration(trip.startAt, trip.endAt),
                                totalDistance: round2d(trip.totalDistance / 1000).format(),
                                hiredDistance: round2d(trip.hiredDistance / 1000).format(),
                                forHireDistance: round2d(trip.onCallDistance / 1000).format(),
                                concession:
                                    trip.concession !== undefined && trip.concession !== null
                                        ? String(trip.concession)
                                        : '',
                                isFlatRate: trip.isFlatRate || false,
                                isEmptyTrip: trip.totalAmount === 0,
                                finalExtrasAmount: trip.finalExtrasAmount
                                    ? `${round2d(trip.finalExtrasAmount / 100).format()}€`
                                    : '--:--',
                                subtractedTotalAmount: (() => {
                                    const subtracted = this.calculateSubtractedAmount(trip);
                                    if (subtracted === 0) return '--:--';
                                    const sign = subtracted > 0 ? '+' : '';
                                    return `${sign}${round2d(subtracted / 100).format()}€`;
                                })(),
                                finalTotalAmount: `${round2d(
                                    trip.finalTotalAmount / 100,
                                ).format()}€`,
                                tips: trip.tip ? `${round2d(trip.tip / 100).format()}€` : '--:--',
                                gesamtumsatz: `${round2d((trip.finalTotalAmount + (trip.tip || 0)) / 100).format()}€`,
                                cashAmount:
                                    trip.paymentMethod === 'cash'
                                        ? `${round2d(trip.finalTotalAmount / 100).format()}€`
                                        : '--:--',
                                vatRate:
                                    trip.vatRate !== undefined && trip.vatRate !== null
                                        ? `${trip.vatRate}%`
                                        : '--:--',
                                ...(trip.paymentMethod && trip.paymentMethod !== 'cash'
                                    ? {
                                          [`paymentMethod_${trip.paymentMethod}`]: `${round2d(
                                              trip.finalTotalAmount / 100,
                                          ).format()}€`,
                                          [`_paymentMethod_${trip.paymentMethod}`]:
                                              trip.finalTotalAmount || 0,
                                      }
                                    : {}),
                                ...Array.from(this.usedPaymentMethods)
                                    .filter(
                                        (method) =>
                                            method !== 'cash' && method !== trip.paymentMethod,
                                    )
                                    .reduce((acc, method) => {
                                        acc[`paymentMethod_${method}`] = '--:--';
                                        acc[`_paymentMethod_${method}`] = 0;
                                        return acc;
                                    }, {}),

                                _startAt: new Date(trip.startAt),
                                _endAt: new Date(trip.endAt),
                                _duration: this.getDurationInMinutes(trip.startAt, trip.endAt),
                                _dauerDez: parseFloat(
                                    this.getDecimalDuration(trip.startAt, trip.endAt),
                                ),
                                _sequenceNumber: trip.sequenceNumber || 0,
                                _totalDistance: trip.totalDistance || 0,
                                _hiredDistance: trip.hiredDistance || 0,
                                _forHireDistance: trip.onCallDistance || 0,
                                _concession:
                                    trip.concession !== undefined && trip.concession !== null
                                        ? String(trip.concession)
                                        : '',
                                _finalExtrasAmount: trip.finalExtrasAmount || 0,
                                _subtractedTotalAmount: this.calculateSubtractedAmount(trip),
                                _finalTotalAmount: trip.finalTotalAmount || 0,
                                _tips: trip.tip || 0,
                                _gesamtumsatz: (trip.finalTotalAmount || 0) + (trip.tip || 0),
                                _cashAmount:
                                    trip.paymentMethod === 'cash' ? trip.finalTotalAmount || 0 : 0,
                                _vatRate:
                                    trip.vatRate !== undefined && trip.vatRate !== null
                                        ? trip.vatRate
                                        : null,

                                originalTrip: trip,
                                isEmptyTrip,
                                hasWarning,
                                isFlatRate,
                                hasGpsData: hasGpsData,
                                isDisabled: tripDriverName === 'Unangemeldet',
                                isUnregisteredDriver: tripDriverName === 'Unangemeldet',
                                _id: `${shiftId}-trip-${index}`,
                                _parentId: shiftId,
                                _isChild: true,
                                _level: 1,
                                _hasChildren: false,
                            };

                            formattedData.push(childRow);
                        });
                }
            });

            if (this.sortState.column) {
                const sortKey = this.sortState.column.startsWith('_')
                    ? this.sortState.column
                    : `_${this.sortState.column}`;

                const parentRows = formattedData.filter((row) => !row._isChild);
                const childRowsByParent = {};

                formattedData.forEach((row) => {
                    if (row._isChild && row._parentId) {
                        if (!childRowsByParent[row._parentId]) {
                            childRowsByParent[row._parentId] = [];
                        }
                        childRowsByParent[row._parentId].push(row);
                    }
                });

                parentRows.sort((a, b) => {
                    const valueA = a[sortKey];
                    const valueB = b[sortKey];
                    return this.compareValues(valueA, valueB, this.sortState.ascending);
                });

                Object.keys(childRowsByParent).forEach((parentId) => {
                    childRowsByParent[parentId].sort((a, b) => {
                        const valueA = a[sortKey];
                        const valueB = b[sortKey];
                        return this.compareValues(valueA, valueB, this.sortState.ascending);
                    });
                });

                const sortedData = [];
                parentRows.forEach((parent) => {
                    sortedData.push(parent);
                    if (parent._expanded && childRowsByParent[parent._id]) {
                        sortedData.push(...childRowsByParent[parent._id]);
                    }
                });

                return sortedData;
            }

            return formattedData;
        },
        legendItems() {
            const items = [
                {
                    key: 'warning',
                    label: 'Fehlbetrag',
                    indicatorClass: 'warning',
                    active: true,
                },
                {
                    key: 'flatrate',
                    label: 'Pauschalpreis',
                    indicatorClass: 'flatrate',
                    active: true,
                },
            ];

            if (!this.hideTripMap) {
                items.push({
                    key: 'gps',
                    label: 'GPS',
                    indicatorClass: 'gps',
                    active: true,
                });
            }

            return items;
        },
    },
    methods: {
        formatDuration(startAt, endAt) {
            const duration = new Date(endAt) - new Date(startAt);
            const hours = Math.floor(duration / (1000 * 60 * 60));
            const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
            return `${hours}h ${minutes}m`;
        },
        getDurationInMinutes(startAt, endAt) {
            const duration = new Date(endAt) - new Date(startAt);
            return Math.floor(duration / (1000 * 60));
        },
        getDecimalDuration(startAt, endAt) {
            const duration = new Date(endAt) - new Date(startAt);
            const totalMinutes = Math.floor(duration / (1000 * 60));
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            const decimalDuration = (hours * 60 + minutes) / 60;
            return round2d(decimalDuration).format();
        },
        compareValues(valueA, valueB, ascending) {
            if (valueA instanceof Date && valueB instanceof Date) {
                return ascending ? valueA - valueB : valueB - valueA;
            }
            if (typeof valueA === 'string' && typeof valueB === 'string') {
                return ascending ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
            }
            return ascending ? valueA - valueB : valueB - valueA;
        },
        handleSortChanged(sort) {
            this.sortState = {
                column: sort.column,
                ascending: sort.ascending,
            };
        },
        handleToggleExpand({ item }) {
            const shiftId = item._id;
            const index = this.expandedShiftIds.indexOf(shiftId);
            if (index >= 0) {
                this.expandedShiftIds.splice(index, 1);
            } else {
                this.expandedShiftIds.push(shiftId);
            }
        },
        handleShiftClick({ item, event }) {
            if (event && event.target.closest('.driver-name')) {
                return;
            }

            if (item._isChild) {
                this.$emit('onTripClick', {
                    shiftUuid: item._parentId,
                    tripUuid: item.originalTrip.uuid,
                });
            }
        },
        openTripDetails(trip) {
            this.$emit('open-trip-details', trip);
        },
        handleDriverFilter(filter) {
            this.$emit('filter', filter);
        },
        handleChangeDriver(shiftOrEvent, currentItem) {
            if (currentItem && !currentItem._isChild) {
                const shiftUuid = currentItem.originalShift?.uuid || currentItem._id;
                const shift = this.shifts.find((s) => s.uuid === shiftUuid);

                if (shift) {
                    this.$emit('changeDriver', shift);
                    return;
                }
            }

            if (shiftOrEvent && shiftOrEvent.driverId) {
                const shift = this.shifts.find(
                    (s) =>
                        s.driver?.id === shiftOrEvent.driverId ||
                        s.driver?.uuid === shiftOrEvent.driverId ||
                        s.driverNumber === shiftOrEvent.driverId,
                );

                if (shift) {
                    this.$emit('changeDriver', shift);
                }
            } else {
                this.$emit('changeDriver', shiftOrEvent);
            }
        },
        handleCarFilter(filter) {
            this.$emit('car-filter', filter);
        },
        handleConcessionFilter(filter) {
            this.$emit('concessionFilter', filter);
        },
        handleChangeCar(carOrEvent) {
            if (carOrEvent && carOrEvent.carId) {
                const shift = this.shifts.find(
                    (s) => s.licenseNumber === carOrEvent.carId || s.car?.uuid === carOrEvent.carId,
                );
                if (shift) {
                    this.$emit('changeCar', shift);
                }
            } else {
                this.$emit('changeCar', carOrEvent);
            }
        },
        calculateSubtractedAmount(entry) {
            if (!entry || entry.finalTotalAmount === undefined) {
                return 0;
            }

            let totalAmount = entry.totalAmount;

            if (totalAmount === undefined && entry.trips && entry.trips.length > 0) {
                totalAmount = entry.trips.reduce((sum, trip) => sum + (trip.totalAmount || 0), 0);
            }

            if (totalAmount === undefined) {
                return 0;
            }

            return entry.finalTotalAmount - totalAmount;
        },
        hasWarning(entry) {
            const subtractedAmount = this.calculateSubtractedAmount(entry);
            return subtractedAmount < 0 || !entry.driver?.name;
        },
        hasError(entry) {
            const drivers = JSON.parse(localStorage.getItem('drivers'));
            const driver = drivers.find((d) => d.id === entry.driverNumber);
            const driverNumberIsName = entry.driver?.name == entry.driver.driverNumber;
            return driverNumberIsName;
        },
        hasTripWarning(entry) {
            if (!entry || entry.finalTotalAmount === undefined || entry.finalTotalAmount === null) {
                return false;
            }

            if (entry.finalTotalAmount === 0) {
                if (
                    entry.totalAmount !== undefined &&
                    entry.totalAmount !== null &&
                    entry.totalAmount > 0
                ) {
                    return true;
                }
                return false;
            }

            if (entry.totalAmount !== undefined && entry.totalAmount !== null) {
                return entry.finalTotalAmount < entry.totalAmount;
            }

            return false;
        },
        handleExpandOnMatch(parentIdsToExpand) {
            if (!Array.isArray(parentIdsToExpand)) return;

            parentIdsToExpand.forEach((id) => {
                if (!this.expandedShiftIds.includes(id)) {
                    this.expandedShiftIds.push(id);
                }
            });
        },
        handleSearchCleared() {
            this.expandedShiftIds = [];
        },
        handleLoadMore() {
            this.currentPage += 1;
        },
        handlePageChanged(page) {
            this.paginationConfig.currentPage = page;
        },
        getDetailsTooltip(item) {
            return 'Details anzeigen';
        },
        openDetailsModal(item) {
            this.selectedShiftForDetails = item;
            this.showDetailsModal = true;
        },
        closeDetailsModal() {
            this.showDetailsModal = false;
            this.selectedShiftForDetails = null;
        },
        openCommentsModal(item) {
            if (item._isChild) return;
            this.selectedShiftForComments = item.originalShift || item;
            this.showCommentsModal = true;
        },
        closeCommentsModal() {
            this.showCommentsModal = false;
            this.selectedShiftForComments = null;
        },
        calculateShiftCashAmount(shift, numericValue = false) {
            if (!shift.trips || shift.trips.length === 0) {
                return numericValue ? 0 : '--:--';
            }

            const cashTotal = shift.trips
                .filter((trip) => trip.paymentMethod === 'cash')
                .reduce((total, trip) => total + (trip.finalTotalAmount || 0), 0);

            if (numericValue) {
                return cashTotal;
            }

            return cashTotal > 0 ? `${round2d(cashTotal / 100).format()}€` : '--:--';
        },
        calculateShiftTips(shift, numericValue = false) {
            if (!shift.trips || shift.trips.length === 0) {
                return numericValue ? 0 : '--:--';
            }

            const tipsTotal = shift.trips.reduce((total, trip) => total + (trip.tip || 0), 0);

            if (numericValue) {
                return tipsTotal;
            }

            return tipsTotal > 0 ? `${round2d(tipsTotal / 100).format()}€` : '--:--';
        },
        getPaymentMethodName(paymentMethod) {
            if (!paymentMethod) return '--:--';
            const method = this.paymentMethods?.find((pm) => pm.value === paymentMethod);
            return method ? method.name : paymentMethod;
        },
        calculatePaymentMethodSums(shift) {
            if (!shift.trips || shift.trips.length === 0) {
                return {};
            }

            const sums = {};
            shift.trips.forEach((trip) => {
                const method = trip.paymentMethod || 'unknown';
                if (!sums[method]) {
                    sums[method] = 0;
                }
                sums[method] += trip.finalTotalAmount || 0;
            });

            return sums;
        },
    },
};
</script>

<style lang="scss" scoped>
.ShiftsTable {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;

    .driver-name {
        cursor: pointer;
        text-decoration: underline;
        font-weight: 500;
        display: block;
        padding: 2px 0;

        &:hover {
            text-decoration: underline;
            color: var(--color-blue);
        }
    }

    .amount-cell {
        display: flex;
        align-items: center;
        gap: 6px;
        justify-content: flex-end;
    }

    .warning-text {
        color: var(--color-red);
        font-weight: 600;
    }

    .positive-text {
        color: var(--color-green-dark, #239d74);
        font-weight: 600;
    }

    .empty-trip-bg {
        background-color: var(--color-gray-light);
        padding: 2px 6px;
        border-radius: 4px;
        font-weight: 500;
        color: var(--color-text);

        &.warning-text {
            color: var(--color-red) !important;
            font-weight: 600 !important;
        }
    }

    .flatrate-trip-bg {
        background-color: var(--color-blue-light);
        padding: 2px 6px;
        border-radius: 4px;
        font-weight: 500;
        color: white !important;

        &.warning-text {
            color: var(--color-red-lighter) !important;
            font-weight: 600 !important;
        }
    }

    .unregistered-driver {
        color: var(--color-text-inactive);
        font-style: italic;
        font-weight: 400;
        display: block;
        padding: 2px 0;
    }

    .Button--LoadMore {
        margin-top: 16px;
        width: 100%;
    }

    .shift-details-modal {
        .detail-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 0;
            border-bottom: 0.5px solid var(--color-gray-light);

            &:last-child {
                border-bottom: none;
            }

            .label {
                font-weight: 600;
                color: var(--color-text-black);
                min-width: 120px;
            }

            .value {
                color: var(--color-text);
                text-align: right;
                flex: 1;
            }
        }
    }
}
</style>
